import {PromptPool} from "../../../../../Server/DB/engineConfigs/@EngineConfig/@EC_Visualization.js";
import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Button, CheckBox, Column, Row, RowLR, Select, Text, TextInput} from "react-vcomponents";
import {ShowMessageBox} from "react-vmessagebox";
import {TextPlus} from "web-vcore";
import {store} from "../../../../../Store/index.js";
import {ElevenLabsVoice, GetElevenLabsVoices} from "../../../../../Utils/Services/ElevenLabs.js";
import {ErrToStr} from "../../../../../Utils/General/Errors.js";
import {PromptPoolsUI_splitAt} from "./PromptPoolUI.js";

export const ElevenLabsVoiceSelector = observer_mgl((props: {pool: PromptPool, enabled?: boolean, ChangePool: (poolChangerFunc: (pool: PromptPool)=>void)=>void})=>{
	const {pool, enabled, ChangePool} = props;
	const [voices, setVoices] = React.useState<ElevenLabsVoice[]>([]);
	const [loading, setLoading] = React.useState(false);
	const [showList, setShowList] = React.useState(false);
	const [playingVoiceId, setPlayingVoiceId] = React.useState<string | null>(null);
	const audioRef = React.useRef<HTMLAudioElement | null>(null);

	const fetchVoices = async()=>{
		const elevenLabsAPIKey = store.main.settings.elevenLabsAPIKey;
		if (!elevenLabsAPIKey) {
			ShowMessageBox({
				title: "ElevenLabs API Key Required",
				message: "Please set your ElevenLabs API key in Settings -> General before listing voices.",
			});
			setShowList(false);
			return;
		}

		setLoading(true);
		try {
			const voiceList = await GetElevenLabsVoices(elevenLabsAPIKey);
			setVoices(voiceList);
		} catch (err) {
			ShowMessageBox({
				title: "Failed to Fetch Voices",
				message: `Error: ${ErrToStr(err)}`,
			});
			setShowList(false);
		} finally {
			setLoading(false);
		}
	};

	const playPreview = (voice: ElevenLabsVoice)=>{
		if (!voice.preview_url) return;

		// Stop current audio if playing
		if (audioRef.current) {
			audioRef.current.pause();
			audioRef.current = null;
		}

		// If clicking the same voice again, just stop
		if (playingVoiceId === voice.voice_id) {
			setPlayingVoiceId(null);
			return;
		}

		// Play new audio
		const audio = new Audio(voice.preview_url);
		audioRef.current = audio;
		setPlayingVoiceId(voice.voice_id);

		audio.onended = ()=>{
			setPlayingVoiceId(null);
			audioRef.current = null;
		};

		audio.onerror = ()=>{
			ShowMessageBox({
				title: "Preview Failed",
				message: "Failed to play voice preview.",
			});
			setPlayingVoiceId(null);
			audioRef.current = null;
		};

		audio.play();
	};

	// Fetch voices when showList is enabled
	React.useEffect(()=>{
		if (showList && voices.length == 0) {
			fetchVoices();
		}
	}, [showList, voices.length]);

	// Cleanup audio on unmount
	React.useEffect(()=>{
		return ()=>{
			if (audioRef.current) {
				audioRef.current.pause();
				audioRef.current = null;
			}
		};
	}, []);

	const showList_final = showList && voices.length > 0;

	return (
		<>
			<RowLR mt={5} splitAt={PromptPoolsUI_splitAt}>
				<TextPlus info="The ElevenLabs voice ID to use. Enable 'List' to browse available voices and preview them.">ElevenLabs voice ID:</TextPlus>
				<Row style={{flex: 1}}>
					<TextInput enabled={enabled} style={{flex: .8}} // bit less space needed, since only id shown here
						value={pool.elevenLabsVoiceId} onChange={val=>ChangePool(c=>c.elevenLabsVoiceId = val)}/>
					<CheckBox ml={5} text={loading ? "Loading..." : "List"} enabled={enabled && !loading} value={showList} onChange={val=>setShowList(val)}/>
					{showList_final &&
						<Select ml={5} enabled={enabled} style={{flex: 1}}
							options={[{name: "(select a voice)", value: ""}, ...voices.map(v=>({name: `${v.name} (${v.voice_id})`, value: v.voice_id}))]}
							value={pool.elevenLabsVoiceId}
							onChange={val=>{
								if (val) {
									ChangePool(c=>c.elevenLabsVoiceId = val);
								}
							}}/>}
					{!showList_final && <div style={{marginLeft: 5, flex: 1}}/>}
				</Row>
			</RowLR>
			{showList_final &&
				<Column mt={5} ml={PromptPoolsUI_splitAt + 5} style={{gap: 5}}>
					<Text style={{fontSize: 12, opacity: 0.7}}>Click a voice to preview:</Text>
					<Column style={{gap: 3, maxHeight: 200, overflowY: "auto"}}>
						{voices.map(voice=>(
							<Row key={voice.voice_id} style={{alignItems: "center", gap: 5}}>
								<Button
									text={playingVoiceId === voice.voice_id ? "⏸ Stop" : "▶ Preview"}
									enabled={enabled && !!voice.preview_url}
									onClick={()=>playPreview(voice)}
									style={{minWidth: 90, fontSize: 11, padding: "3px 8px"}}
								/>
								<Text style={{fontSize: 12, cursor: voice.preview_url ? "pointer" : "default"}} onClick={()=>voice.preview_url && playPreview(voice)}>
									{voice.name} <span style={{marginLeft: 5, opacity: 0.5}}>({voice.voice_id})</span>
								</Text>
							</Row>
						))}
					</Column>
				</Column>}
		</>
	);
});